import { type FC } from "react";
import { cn } from "../../utils/cn";
import { Button } from "../../primitives/button";
import { BasePageHeader } from "../../components/page-header/BasePageHeader";
import type { QuoteHeaderProps, QuoteHeaderAction } from "./types";

const ActionButton: FC<{ action: QuoteHeaderAction }> = ({ action }) => {
  const Icon = action.icon;

  const buttonVariant =
    action.variant === "primary" ? "soft-primary" : "soft-destructive";

  return (
    <Button
      onClick={action.onClick}
      variant={buttonVariant}
      size="sm"
      className="font-semibold px-4 h-9 border-transparent"
    >
      {Icon && <Icon className="w-4 h-4 mr-2" />}
      {action.label}
    </Button>
  );
};

export const QuoteHeader: FC<QuoteHeaderProps> = ({
  title,
  badges,
  projectName,
  metadata,
  actions,
  description,
}) => {
  const mappedBadges = badges?.map((badge) => ({
    label: badge.label,
    icon: badge.icon,
    className: cn(
      badge.variant === "primary" &&
        "bg-primary/10 text-primary border-transparent",
      badge.variant === "secondary" &&
        "bg-gray-50 text-gray-600 border-transparent",
      badge.variant === "success" &&
        "bg-green-50 text-green-600 border-transparent",
      badge.variant === "public" &&
        "bg-green-50 text-green-600 border-transparent",
      badge.variant === "private" &&
        "bg-gray-100 text-gray-600 border-transparent",
      badge.className,
    ),
  }));

  const actionElements =
    actions && actions.length > 0 ? (
      <div className="flex items-center gap-2">
        {actions.map((action, index) => (
          <ActionButton key={`${action.label}-${index}`} action={action} />
        ))}
      </div>
    ) : undefined;

  return (
    <BasePageHeader
      title={title}
      badges={mappedBadges}
      projectName={projectName}
      metadata={metadata}
      actions={actionElements}
      description={description}
    />
  );
};

QuoteHeader.displayName = "QuoteHeader";
